import { useMemo, useState } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import {
  activeChild,
  childQuotes,
  planTitle,
  proposedPlan,
  rubricHistory,
  rubricMoves,
  stalledSkills,
  type SkillMove,
} from '../../store/selectors'
import { formatRelative } from '../../lib/dates'
import { LookWhatICanDo } from './LookWhatICanDo'
import { Sheet } from '../ui/Sheet'

/**
 * The ceremony at the end of a cycle.
 *
 * Six weeks of ten-minute mornings do not feel like anything from inside them.
 * This is the moment that makes them count — so it opens with the child, not
 * with a table, and the numbers arrive only once the story has been told.
 *
 * Three honest sections, in this order: what moved, what did not, and what we
 * do about it. The middle one is not softened. A parent who is told everything
 * is going well learns nothing, and the whole system depends on them believing
 * this thing when it says something is stuck.
 */
export function CycleReview({ onBack }: { onBack?: () => void }) {
  const child = useAuroraStore(activeChild)
  const history = useAuroraStore(rubricHistory) as any[]
  const stalled = useAuroraStore(stalledSkills)
  const quotes = useAuroraStore(childQuotes)
  const title = useAuroraStore(planTitle)
  const proposal = useAuroraStore(proposedPlan) as any
  const [book, setBook] = useState(false)

  const name = (child as any)?.name ?? 'they'
  const before = history[0]
  const after = history[history.length - 1]
  const firstEver = history.length < 2

  const moves = useMemo(
    () => (firstEver ? [] : rubricMoves(before?.data, after?.data)),
    [firstEver, before, after],
  )

  const grew = useMemo(
    () =>
      moves
        .filter((m) => typeof m.delta === 'number' && m.delta > 0)
        .sort((a, b) => (b.delta ?? 0) - (a.delta ?? 0))
        .slice(0, 6),
    [moves],
  )

  const newlySeen = useMemo(
    () => moves.filter((m) => m.from === undefined && typeof m.to === 'number').slice(0, 5),
    [moves],
  )

  const held = useMemo(
    () => moves.filter((m) => m.delta === 0 && (m.trend === 'stalled' || m.note)).slice(0, 4),
    [moves],
  )

  const ask = (text: string) => {
    const s = auroraStore.getState() as any
    s.setComposing(text)
    s.setView('thread')
  }

  const span = before && after ? formatRelative(before.createdAt ?? before.updatedAt) : null

  return (
    <div className="review">
      {onBack && (
        <button className="week-back" onClick={onBack}>
          <BackIcon /> Back
        </button>
      )}

      <header className="review-head">
        <div className="review-sky" aria-hidden="true" />
        <p className="review-eyebrow">{title ?? 'This cycle'}</p>
        <h3 className="review-title">
          {firstEver ? `Where ${name} is starting` : `What ${name} can do now`}
        </h3>
        {span && !firstEver && <p className="review-span">Measured against {span}</p>}
      </header>

      {quotes.length > 0 && (
        <figure className="review-quote">
          <blockquote>“{quotes[0]!.quote}”</blockquote>
          <figcaption>
            {name}, {formatRelative(quotes[0]!.at)}
          </figcaption>
        </figure>
      )}

      {firstEver ? (
        <p className="review-empty">
          This is the first picture, so there is nothing to compare it to yet. Come back at the end
          of the cycle: this page is where six weeks of ten-minute mornings turn into something you
          can see.
        </p>
      ) : (
        <>
          <section className="review-section">
            <h4 className="section-title">What moved</h4>
            {grew.length === 0 ? (
              <p className="review-note">
                No level changed this cycle. That is normal for a short run, and it does not mean
                nothing happened. Look at what {name} said and made.
              </p>
            ) : (
              <ul className="moves">
                {grew.map((m) => (
                  <MoveRow key={`${m.domain}:${m.name}`} move={m} />
                ))}
              </ul>
            )}
          </section>

          {newlySeen.length > 0 && (
            <section className="review-section">
              <h4 className="section-title">Looked at for the first time</h4>
              <ul className="moves">
                {newlySeen.map((m) => (
                  <li key={`${m.domain}:${m.name}`} className="move is-new">
                    <span className="move-name">{m.name}</span>
                    <span className="move-domain">{m.domain}</span>
                    <span className="move-level tnum">now {m.to}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {(stalled.length > 0 || held.length > 0) && (
        <section className="review-section">
          <h4 className="section-title">Still stuck</h4>
          <p className="review-note">
            Tried, repeatedly, without movement. Each of these wants a different approach rather
            than more practice.
          </p>
          <ul className="stuck">
            {stalled.map(({ domain, skill }) => (
              <li key={`${domain}:${skill.key}`}>
                <span className="stuck-name">{skill.name}</span>
                {skill.note && <span className="stuck-note">{skill.note}</span>}
              </li>
            ))}
            {held
              .filter((h) => !stalled.some((s) => s.skill.name === h.name))
              .map((m) => (
                <li key={`held:${m.name}`}>
                  <span className="stuck-name">{m.name}</span>
                  {m.note && <span className="stuck-note">{m.note}</span>}
                </li>
              ))}
          </ul>
        </section>
      )}

      <section className="review-section review-next">
        <h4 className="section-title">What comes next</h4>

        {proposal ? (
          <div className="proposal">
            <p className="proposal-title">{proposal.title}</p>
            {proposal.summary && <p className="proposal-why">{proposal.summary}</p>}
            {Array.isArray(proposal.data?.principles) && proposal.data.principles.length > 0 && (
              <ul className="proposal-reasons">
                {proposal.data.principles.slice(0, 4).map((p: string) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            )}
            <div className="row row-2">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => ask(`Start the next cycle: ${proposal.title}`)}
              >
                Talk it through
              </button>
              <button
                className="btn btn-quiet btn-sm"
                onClick={() => ask('What would you change about the next cycle?')}
              >
                Push back on it
              </button>
            </div>
          </div>
        ) : (
          <div className="proposal is-empty">
            <p className="proposal-why">
              Sapling writes the next cycle from this page: what moved, what did not, and what it
              has learned about how {name} actually works.
            </p>
            <button
              className="btn btn-primary"
              onClick={() =>
                ask(
                  stalled.length
                    ? `Propose the next cycle. Do something different about ${stalled
                        .slice(0, 2)
                        .map((s) => s.skill.name.toLowerCase())
                        .join(' and ')}.`
                    : 'Propose the next cycle',
                )
              }
            >
              Propose the next cycle
            </button>
          </div>
        )}
      </section>

      <div className="review-foot">
        <button className="btn btn-ghost" onClick={() => setBook(true)}>
          <BookIcon /> Make the book
        </button>
        <p className="review-foot-note">
          Her words, her work, and what she learned to do, as something you can print and keep.
        </p>
      </div>

      <Sheet open={book} onClose={() => setBook(false)} title="Look what I can do" wide>
        <LookWhatICanDo onClose={() => setBook(false)} />
      </Sheet>
    </div>
  )
}

function MoveRow({ move }: { move: SkillMove }) {
  const steps = move.delta ?? 0
  return (
    <li className="move">
      <span className="move-name">{move.name}</span>
      <span className="move-domain">{move.domain}</span>
      <span className="move-bar" aria-hidden="true">
        <span className="move-from tnum">{move.from}</span>
        <span className="move-arrow" />
        <span className="move-to tnum">{move.to}</span>
      </span>
      <span className="sr-only">
        {move.name} moved from level {move.from} to level {move.to}
      </span>
      {steps > 1 && <span className="move-jump">+{steps}</span>}
    </li>
  )
}

const S = {
  viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true,
}
const BackIcon = () => <svg {...S} width="15" height="15"><path d="M15 6l-6 6 6 6" /></svg>
const BookIcon = () => (
  <svg {...S} width="16" height="16" strokeWidth={1.75}>
    <path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v16H6.5A2.5 2.5 0 0 0 4 20.5z" />
    <path d="M4 20.5A2.5 2.5 0 0 1 6.5 18H20v4H6.5A2.5 2.5 0 0 1 4 19.5z" />
  </svg>
)
